/* These mixins are designed to work with AP/PC/JE List Views
 * Assumes parent component has all necessary data elements
 * e.g. Vuex store, ElementUI, etc.
*/
import { mapGetters } from 'vuex'
import {
  createAuditReport,
  createPostingReport,
  batchCreateAuditReport,
  batchCreatePostingReport,
  batchRunPostingReport,
  createBillPdf
} from '@/utils/helpers/DataEntryViews/CreateReport'
import { formatLocale } from '@/utils/helpers/StringsNumbers'
import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'

export const listViewMixins = {
  data () {
    return {
      batchUpdateSelection: null,
      disableAuditBtn: false,
      changingPage: false
    }
  },
  methods: {
    /**
     * Asks the grid for a page of the list, with the column filters it holds applied on top of this
     * view's own criteria. Every load goes through the grid - entering the view, paging and
     * changing tab - so the query is built one way only.
     * @param page
     */
    loadRows (page = 1, extendedFilters = {}) {
      const grid = this.$refs['transactions_conf_list']
      return grid ? grid.loadRows(page, null, extendedFilters) : null
    },
    /**
     * Creates an audit report when a record's 'Audit' button is clicked
     */
    handleAuditBtnClick (transaction, type) {
      if (!transaction.hasOwnProperty('transactionType') || !transaction.transactionType) {
        transaction['transactionType'] = type
      }
      switch (type) {
        case 'Accounts Receivable':
          createBillPdf(this, transaction, 'AUDITED')
          break
        case 'Bill Payment':
          this.$store.dispatch('updateBp', { id: transaction.id, data: { status: 'AUDITED' } })
          break
        case 'Pre Payment':
          this.$store.dispatch('updatePp', { id: transaction.id, data: { status: 'AUDITED' } })
          break
        default:
          createAuditReport(this, transaction)
      }
    },
    /**
     * Creates a posting report when a record's 'Post' button is clicked
     */
    handlePostBtnClick (transaction) {
      switch (transaction.transactionType) {
        case 'Accounts Receivable':
          createBillPdf(this, transaction, ['POSTED', 'CLOSED'].includes(transaction.status) ? null : 'POSTED')
          break
        case 'Bill Payment':
          this.$store.dispatch('updateBp', { id: transaction.id, data: { status: 'POSTED' } })
          break
        case 'Pre Payment':
          this.$store.dispatch('updatePp', { id: transaction.id, data: { status: 'POSTED' } })
          break
        default:
          createPostingReport(this, transaction)
      }
    },
    /**
     * Handles batch selections
     */
    async handleBatchUpdateSelection (selectedOpt = null) {
      try {
        const filtered = this.selection.filter(v => ['OPEN', 'AUDITED'].includes(v.status))
        await this.$store.dispatch('updateFilteredTransactionSelection', filtered)
        switch (selectedOpt) {
          case 0:
            batchCreateAuditReport(this)
            break
          case 1:
            batchCreatePostingReport(this)
            break
          case 2:
            batchCreateAuditReport(this, null, 'BATCH')
            break
          case 3:
            batchRunPostingReport(this, null, 'BATCH')
            break
        }
        if (this.$refs.listViewHeader) {
          this.$refs.listViewHeader.batchUpdateSelection = null
        }
      } catch (e) {
        console.log(e)
      }
    },
    /**
     * Handles showing & hiding filters
     */
    toggleFilters () {
      !this.showFilters ? this.showFilters = true : this.showFilters = false
    },
    /**
     * Preserves the selected transactions and shows them as selected whenever the table is re-rendered
     * due to a page or tab change
     */
    restoreSelection (tableRef) {
      let table = this.$refs[tableRef]
      if (table) {
        const thisPageSelectedIds = this.selection
          .filter(v => v.page === this.currentPage)
          .map(v => table.data.find(x => x.id === v.id))
        thisPageSelectedIds.forEach(v => table.toggleRowSelection(v, true))
      }
    },
    /**
     * Looks through the current selection to check if a specified transaction is selected
     * @param id
     * @return {T|*}
     */
    checkIfTransactionIsSelected (id) {
      return this.selection.find(trx => trx.id === id)
    },
    /**
     * Determines whether the audit button should be disabled
     * @param id
     * @return {boolean}
     */
    checkIfBtnShouldBeDisabled (id) {
      return !!(this.disableAuditBtn && this.checkIfTransactionIsSelected(id))
    },
    /**
     * Formats dollar amounts with thousands separators
     * @param value
     * @return {*}
     */
    formatAmount (value) {
      return value ? formatLocale(value) : ''
    }
  },
  computed: {
    ...mapGetters({
      filteredSelection: 'getFilteredSelectedTransactions',
      navigating: 'getNavigationStatus',
      isFilterLoading: 'getTransactionFilterStatus',
      currentFilters: 'getFilters',
      selection: 'getSelectedTransactions'
    }),
    /**
     * Computed property that returns a boolean indicating whether the user is permitted to post a transaction
     * @return {*|boolean}
     */
    userCanPost () {
      if (this.$store.getters.checkIfUserHasRequiredPermissions(['auth.post_transactions'])) {
        return true
      } else {
        let permission = 'entry.post_accountspayable'
        switch (this.$route.name.toLowerCase()) {
          case 'aps':
            permission = 'entry.post_accountspayable'
            break
          case 'jes':
            permission = 'entry.post_journal'
            break
          case 'prs':
            permission = 'entry.post_payrollentry'
            break
          case 'pcs':
            permission = 'entry.post_pettycash'
            break
        }
        return this.$store.getters.checkIfUserHasRequiredPermissions([permission])
      }
    },
    /**
     * Computed property that returns a boolean indicating whether the user is permitted to audit a transaction
     * @return {*|boolean}
     */
    userCanAudit () {
      let permissions = ['entry.view_accountspayable']
      switch (this.$route.name.toLowerCase()) {
        case 'aps':
          permissions = ['entry.view_accountspayable']
          break
        case 'jes':
          permissions = ['entry.view_journalentry']
          break
        case 'prs':
          permissions = ['entry.view_payrollentry']
          break
        case 'pcs':
          permissions = ['entry.view_pettycash']
          break
      }
      return this.$store.getters.checkIfUserHasRequiredPermissions(permissions)
    },
    /**
     * Computed property that returns a list of options available when multiple transactions have been selected
     * This is used when the user selects one or more transactions and attempts to
     * use one of the batch update options from the "With Selected" dropdown menu.
     * In this case, if the user's selection contains nothing but transactions with
     * statuses that are not auditable or postable, then options to generate an audit
     * report or a posting report are disabled.
     * @return {array}
     */
    batchUpdateOptions () {
      let allUnpostable = this.selection.every(v => ['DRAFT', 'POSTED', 'CLOSED', 'VOIDED'].includes(v.status))
      let isPostingReportRunnable = this.selection.every(v => ['POSTED', 'CLOSED'].includes(v.status))
      let isAuditReportRunnable = this.selection.every(v => ['AUDITED', 'OPEN'].includes(v.status))
      return [
        {
          text: 'Audit transactions',
          type: 'audit',
          disabled: allUnpostable || !this.userCanAudit
        },
        {
          text: 'Post open and audited transactions',
          type: 'post',
          disabled: allUnpostable || !this.userCanPost
        },
        {
          text: 'Audit Report',
          type: 'audit-report',
          disabled: !isAuditReportRunnable || !this.hasPermission.read
        },
        {
          text: 'Posting Report',
          type: 'post-report',
          disabled: !isPostingReportRunnable || !this.hasPermission.read
        }
      ]
    }
  },
  /**
   * Clear filters, as well as transaction type state to prevent over-caching and stale data
   * @param to
   * @param from
   * @param next
   * @return {Promise<void>}
   */
  async beforeRouteLeave (to, from, next) {
    let type = capitalizeFirstLetter(this.$route.name.toLowerCase().slice(0, -1))
    if (from.name === 'PRs') {
      type = 'Je'
    }
    if (from.name === 'Proscan') {
      type = 'ProscanLog'
    }
    try {
      let promises = [this.$store.dispatch('clearFilters')]
      if (to.name !== `Edit${type}` || to.name !== `New${type}`) {
        promises = [...promises, ...[
          this.$store.dispatch(`clear${type}State`),
          this.$store.dispatch(`reset${type}Meta`)
        ]]
      }
      await Promise.all(promises)
    } catch (e) {
      console.log(e)
    } finally {
      next()
    }
  }
}
